import { useState, useMemo } from 'react';
import { PageHeader } from '@/components/layout/page-header';
import { StatusBadge, type StatusType } from '@/components/ui/status-badge';
import { Pagination } from '@/components/ui/pagination';
import {
  SlideOverDrawer,
  DrawerHeader,
  DrawerBody,
} from '@/components/ui/slide-over-drawer';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  TableLoading,
} from '@/components/ui/table';
import { useTelviaIQOrders } from '@/features/dids';
import type { TelviaIQOrder, TelviaIQOrderStatus, TelviaIQOrderType } from '@/features/dids/types';
import { formatPhoneNumber } from '@/lib/utils';
import {
  ClipboardList,
  Search,
  ChevronRight,
  X,
} from 'lucide-react';

// Order types for dropdown
const ORDER_TYPES: { value: string; label: string }[] = [
  { value: '', label: 'All Actions' },
  { value: 'DID_ORDER', label: 'Order DID' },
  { value: 'DID_RELEASE', label: 'Release DID' },
  { value: 'PORT_SUBMISSION', label: 'Port Submission' },
  { value: 'FORWARD_ENABLE', label: 'Enable Forwarding' },
  { value: 'FORWARD_DISABLE', label: 'Disable Forwarding' },
  { value: 'FAILOVER_ENABLE', label: 'Enable Failover' },
  { value: 'FAILOVER_DISABLE', label: 'Disable Failover' },
  { value: 'E911_ADD', label: 'Add E911' },
  { value: 'E911_UPDATE', label: 'Update E911' },
  { value: 'E911_REMOVE', label: 'Remove E911' },
  { value: 'CNAM_ENABLE', label: 'Enable CNAM' },
  { value: 'CNAM_DISABLE', label: 'Disable CNAM' },
  { value: 'CNAM_UPDATE', label: 'Update CNAM' },
  { value: 'CONFERENCE_ENABLE', label: 'Enable Conference' },
  { value: 'CONFERENCE_DISABLE', label: 'Disable Conference' },
  { value: 'FAX_ENABLE', label: 'Enable Fax' },
  { value: 'FAX_DISABLE', label: 'Disable Fax' },
];

// Status options for dropdown
const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All Statuses' },
  { value: 'SUCCESS', label: 'Success' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' },
];

// Map order status to StatusBadge type
function getStatusType(status: TelviaIQOrderStatus): StatusType {
  switch (status) {
    case 'SUCCESS':
      return 'completed';
    case 'FAILED':
      return 'rejected';
    case 'PENDING':
      return 'pending';
    default:
      return 'pending';
  }
}

// Get status label
function getStatusLabel(status: TelviaIQOrderStatus): string {
  switch (status) {
    case 'SUCCESS':
      return 'Success';
    case 'FAILED':
      return 'Failed';
    case 'PENDING':
      return 'Pending';
    default:
      return 'Unknown';
  }
}

// Format datetime for display
function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch {
    return dateString;
  }
}

// Get action category color
function getActionColor(orderType: TelviaIQOrderType): string {
  if (orderType.includes('ENABLE') || orderType === 'DID_ORDER' || orderType.includes('ADD')) {
    return 'text-cyan-600';
  }
  if (orderType.includes('DISABLE') || orderType === 'DID_RELEASE' || orderType.includes('REMOVE')) {
    return 'text-rose-600';
  }
  if (orderType.includes('UPDATE')) {
    return 'text-amber-600';
  }
  return 'text-slate-600';
}

// Format details from old_value/new_value
function formatDetails(order: TelviaIQOrder): string | null {
  const { order_type, old_value, new_value } = order;

  if (order_type === 'FORWARD_ENABLE' && new_value) {
    return `Fwd set to ${formatPhoneNumber(new_value)}`;
  }
  if (order_type === 'FORWARD_DISABLE' && old_value) {
    return `Fwd removed (was ${formatPhoneNumber(old_value)})`;
  }
  if (order_type === 'FAILOVER_ENABLE' && new_value) {
    return `Failover set to ${formatPhoneNumber(new_value)}`;
  }
  if (order_type === 'FAILOVER_DISABLE' && old_value) {
    return `Failover removed (was ${formatPhoneNumber(old_value)})`;
  }
  if (order_type === 'CNAM_UPDATE' && new_value) {
    return `Caller ID set to "${new_value}"`;
  }
  if (order_type === 'CNAM_ENABLE') {
    return 'CNAM enabled: "Enabled"';
  }
  if (order_type === 'E911_UPDATE' && new_value) {
    return `Updated to: ${new_value}`;
  }
  if (order_type === 'E911_ADD' && new_value) {
    return `Address: ${new_value}`;
  }

  return null;
}

// Order row component
interface OrderRowProps {
  order: TelviaIQOrder;
  onRowClick: () => void;
}

function OrderRow({ order, onRowClick }: OrderRowProps) {
  const details = formatDetails(order);

  return (
    <TableRow
      className="cursor-pointer hover:bg-slate-50/80 transition-colors"
      onClick={onRowClick}
    >
      {/* DATE/TIME */}
      <TableCell className="px-4">
        <span className="text-sm text-slate-700">{formatDateTime(order.created_at)}</span>
      </TableCell>

      {/* ACTION */}
      <TableCell className="px-4">
        <span className={`text-sm font-medium ${getActionColor(order.order_type)}`}>
          {order.order_type_display}
        </span>
      </TableCell>

      {/* PHONE NUMBER */}
      <TableCell className="px-4">
        <span className="font-mono text-sm text-indigo-600">
          {order.tn ? formatPhoneNumber(order.tn) : '-'}
        </span>
      </TableCell>

      {/* STATUS */}
      <TableCell className="px-4">
        <StatusBadge
          status={getStatusType(order.status)}
          label={getStatusLabel(order.status)}
        />
      </TableCell>

      {/* USER */}
      <TableCell className="px-4">
        <span className="text-sm text-slate-600">{order.username || '-'}</span>
      </TableCell>

      {/* DETAILS */}
      <TableCell className="px-4">
        <span className="text-sm text-slate-500">
          {details || '-'}
        </span>
      </TableCell>

      {/* ACTIONS */}
      <TableCell className="px-4 text-right">
        <ChevronRight size={16} className="text-slate-400 inline" />
      </TableCell>
    </TableRow>
  );
}

// Order detail drawer
interface OrderDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  order: TelviaIQOrder | null;
}

function OrderDetailDrawer({ isOpen, onClose, order }: OrderDetailDrawerProps) {
  if (!order) return null;

  const details = formatDetails(order);

  return (
    <SlideOverDrawer isOpen={isOpen} onClose={onClose} width="md">
      <DrawerHeader
        title="Order Details"
        onClose={onClose}
      />

      <DrawerBody className="flex flex-col">
        {/* Order Summary */}
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-500">Action</span>
            <span className={`text-sm font-medium ${getActionColor(order.order_type)}`}>
              {order.order_type_display}
            </span>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-500">Phone Number</span>
            <span className="font-mono text-sm text-indigo-600">
              {order.tn ? formatPhoneNumber(order.tn) : '-'}
            </span>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-500">Date/Time</span>
            <span className="text-sm text-slate-700">{formatDateTime(order.created_at)}</span>
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-500">Status</span>
            <StatusBadge
              status={getStatusType(order.status)}
              label={getStatusLabel(order.status)}
            />
          </div>

          <div className="flex items-center justify-between py-2 border-b border-slate-100">
            <span className="text-sm text-slate-500">User</span>
            <span className="text-sm text-slate-700">{order.username || '-'}</span>
          </div>

          {order.old_value && (
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Previous Value</span>
              <span className="text-sm text-slate-700 font-mono">{order.old_value}</span>
            </div>
          )}

          {order.new_value && (
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">New Value</span>
              <span className="text-sm text-slate-700 font-mono">{order.new_value}</span>
            </div>
          )}

          {details && (
            <div className="py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500 block mb-1">Details</span>
              <span className="text-sm text-slate-700">{details}</span>
            </div>
          )}

          {order.error_message && (
            <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg">
              <p className="text-sm font-medium text-rose-700 mb-1">Error</p>
              <p className="text-sm text-rose-600">{order.error_message}</p>
            </div>
          )}

          {/* Port order specific fields */}
          {order.port_dids && order.port_dids.length > 0 && (
            <div className="py-2">
              <span className="text-sm text-slate-500 block mb-2">
                DIDs ({order.port_dids.length})
              </span>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {order.port_dids.map((tn) => (
                  <div key={tn} className="font-mono text-sm text-indigo-600 py-1">
                    {formatPhoneNumber(tn)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {order.signer_name && (
            <div className="flex items-center justify-between py-2 border-b border-slate-100">
              <span className="text-sm text-slate-500">Signer</span>
              <span className="text-sm text-slate-700">
                {order.signer_name}
                {order.signer_company && ` (${order.signer_company})`}
              </span>
            </div>
          )}
        </div>
      </DrawerBody>
    </SlideOverDrawer>
  );
}

export function OrderHistoryPage() {
  // Filter state
  const [actionFilter, setActionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [phoneFilter, setPhoneFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Drawer state
  const [selectedOrder, setSelectedOrder] = useState<TelviaIQOrder | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Build filters object for API
  const filters = useMemo(() => ({
    orderType: actionFilter || undefined,
    status: statusFilter || undefined,
    tn: phoneFilter || undefined,
    user: userFilter || undefined,
    limit: 500,
  }), [actionFilter, statusFilter, phoneFilter, userFilter]);

  // Fetch orders with filters
  const { data: allOrders = [], isLoading, error } = useTelviaIQOrders(filters);

  // Count active filters
  const activeFilterCount = [actionFilter, statusFilter, phoneFilter, userFilter].filter(Boolean).length;

  // Clear all filters
  const clearFilters = () => {
    setActionFilter('');
    setStatusFilter('');
    setPhoneFilter('');
    setUserFilter('');
    setCurrentPage(1);
  };

  // Paginate
  const totalItems = allOrders.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const paginatedOrders = allOrders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Get unique users for user dropdown
  const uniqueUsers = useMemo(() => {
    const users = new Set<string>();
    allOrders.forEach(order => {
      if (order.username) users.add(order.username);
    });
    return Array.from(users).sort();
  }, [allOrders]);

  // Handle row click
  const handleRowClick = (order: TelviaIQOrder) => {
    setSelectedOrder(order);
    setIsDrawerOpen(true);
  };

  return (
    <div>
      <PageHeader title="Order History" />

      <div className="p-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Activity Orders Log</h2>
            <p className="text-sm text-slate-500 mt-1">
              {totalItems} records
            </p>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Filter Bar */}
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="flex flex-wrap items-center gap-3">
              {/* Action Dropdown */}
              <select
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-[160px]"
              >
                {ORDER_TYPES.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {/* Phone Number Search */}
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  placeholder="Phone number..."
                  value={phoneFilter}
                  onChange={(e) => {
                    setPhoneFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-40 pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              {/* Status Dropdown */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent min-w-[130px]"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              {/* User Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="User..."
                  value={userFilter}
                  onChange={(e) => {
                    setUserFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  list="user-options"
                  className="w-32 px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <datalist id="user-options">
                  {uniqueUsers.map(user => (
                    <option key={user} value={user} />
                  ))}
                </datalist>
              </div>

              {/* Clear Filters Button */}
              {activeFilterCount > 0 && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X size={14} />
                  Clear ({activeFilterCount})
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-4 w-36">DATE/TIME</TableHead>
                <TableHead className="px-4 w-44">ACTION</TableHead>
                <TableHead className="px-4 w-36">PHONE NUMBER</TableHead>
                <TableHead className="px-4 w-24">STATUS</TableHead>
                <TableHead className="px-4 w-36">USER</TableHead>
                <TableHead className="px-4">DETAILS</TableHead>
                <TableHead className="px-4 w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableLoading colSpan={7} rows={10} />
              ) : error ? (
                <TableEmpty
                  colSpan={7}
                  message="Error loading orders. Please try again."
                  icon={<ClipboardList className="h-8 w-8 text-red-300" />}
                />
              ) : paginatedOrders.length === 0 ? (
                <TableEmpty
                  colSpan={7}
                  message={
                    activeFilterCount > 0
                      ? 'No orders match your filters'
                      : 'No activity yet.'
                  }
                  icon={<ClipboardList className="h-8 w-8 text-slate-300" />}
                />
              ) : (
                paginatedOrders.map((order) => (
                  <OrderRow
                    key={order.id}
                    order={order}
                    onRowClick={() => handleRowClick(order)}
                  />
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            className="border-t border-slate-100"
          />
        </div>
      </div>

      {/* Order Detail Drawer */}
      <OrderDetailDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
      />
    </div>
  );
}
